import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { SessionStorageConstants } from '../models/common/session-storage-constants';
import { UserModel } from '../models/user-model';
import { getBypassApManager1FromUserRoles } from '../utils/ap-manager-bypass.util';
import {
  persistFullUserRolesCache,
  resolveCachedFullUserForMerge,
  withPreservedUserRoles,
} from '../utils/user-roles-merge.util';
import { ContextService } from './context.service';
import { SessionStorageService } from './sessions-storage.service';
import { UserService } from './user.service';

/**
 * Loads `/me` into the app context: user + role list, company modules / syncs / additional
 * configurations / platform, and the role's BE permissions. Used by ContextGuard on navigation and
 * when the backend reports an organization change (SSE), so the change applies without a reload.
 */
@Injectable({ providedIn: 'root' })
export class UserContextRefreshService {
  constructor(
    private userSvc: UserService,
    private contextSvc: ContextService,
    private sessionStorageService: SessionStorageService
  ) {}

  /** GET `/me` and apply it. */
  refresh(): Observable<UserModel> {
    return this.userSvc.getMyAsync().pipe(map((user) => this.apply(user)));
  }

  apply(user: UserModel): UserModel {
    const selectedRoleId = localStorage.getItem('selectedRoleId');
    const sessionCachedUser = this.sessionStorageService.getItem(
      SessionStorageConstants.MULTIPLE_USER
    ) as UserModel | null;
    const cachedFullUser = resolveCachedFullUserForMerge(
      user,
      this.contextSvc.multipleUsers.value,
      sessionCachedUser
    );

    // `/my` is role-scoped via Role header — keep the full Switch Role list.
    const fullUser = withPreservedUserRoles(user, cachedFullUser);
    persistFullUserRolesCache(fullUser);

    const filteredUser = {
      ...fullUser,
      userRoles: selectedRoleId
        ? fullUser.userRoles.filter((r) => r.roleId === selectedRoleId)
        : fullUser.userRoles,
    };

    const companyId =
      filteredUser.userRoles?.[0]?.company?.id ??
      this.contextSvc.getCompanyId() ??
      localStorage.getItem('selectedCompanyId');

    // Additional Configurations come with /me now; no separate /v1/configuration/search call.
    this.contextSvc.setBypassApManager1(
      getBypassApManager1FromUserRoles(fullUser.userRoles, companyId)
    );

    this.contextSvc.user.next(filteredUser);
    this.contextSvc.multipleUsers.next(fullUser);

    if (selectedRoleId) {
      this.contextSvc.saveApRoleCompanyPermissions(
        selectedRoleId,
        user.companyPermissions ?? []
      );
    }

    this.sessionStorageService.setItem(SessionStorageConstants.USER, filteredUser);
    this.sessionStorageService.setItem(SessionStorageConstants.MULTIPLE_USER, fullUser);

    return filteredUser;
  }
}
